import { useEffect } from 'react'
import { ChevronLeft, Gauge, Snowflake, Thermometer, WifiOff, X, Zap } from '../../icons'
import type { Sensor, SensorIcon } from '../../types/device'
import { formatKW } from '../../lib/format'
import { PanelShell } from './PanelShell'

interface Props {
  sensor: Sensor
  onClose: () => void
  onBack: (() => void) | null
  showClose: boolean
  asSheet: boolean
}

function KindIcon({ name }: { name: SensorIcon }) {
  switch (name) {
    case 'Snowflake': return <Snowflake />
    case 'Zap': return <Zap />
    case 'Thermometer': return <Thermometer />
    default: return <Thermometer />
  }
}

function ageLabel(ageMinutes: number | null): string {
  if (ageMinutes == null) return 'nessuna lettura disponibile'
  if (ageMinutes < 1) return 'pochi secondi fa'
  if (ageMinutes < 60) return `${ageMinutes} minuti fa`
  const hours = Math.floor(ageMinutes / 60)
  if (hours < 24) return `${hours} ore fa`
  return `${Math.floor(hours / 24)} giorni fa`
}

function lastSeenAbsolute(lastSeenMs: number | null): string {
  if (lastSeenMs == null) return '—'
  const d = new Date(lastSeenMs)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value))
}

export function SensorDetails({ sensor, onClose, onBack, showClose, asSheet }: Props) {
  const state = sensor.online ? 'on' : 'offline'
  const r = sensor.readings

  useEffect(() => {
    if (!showClose) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, showClose])

  const isElectrical = sensor.kind === 'elettrico'

  const body = (
    <div className="dpanel" data-state={state}>
      {onBack && (
        <button className="dpanel__back" type="button" onClick={onBack}>
          <ChevronLeft />
          <span>Panoramica sito</span>
        </button>
      )}
      <div className="dpanel__header">
        <div className="dpanel__title-wrap">
          <div className="dpanel__icon">
            <KindIcon name={sensor.icon} />
          </div>
          <div>
            <div className="dpanel__name">{sensor.name}</div>
            <div className="dpanel__id">{sensor.macColon}</div>
          </div>
        </div>
        {showClose && (
          <button className="modal__close" onClick={onClose} aria-label="Chiudi">
            <X />
          </button>
        )}
      </div>

      <div className="dpanel__tag-row">
        <span className="dpanel__tag dpanel__tag--meter">
          <Gauge />
          <span>{sensor.kind === 'cella-frigo' ? 'Cella frigo' : sensor.kind === 'elettrico' ? 'Quadro elettrico' : 'Qualità aria'}</span>
        </span>
        <span className="dpanel__tag dpanel__tag--muted">Solo misurazione</span>
      </div>

      {!sensor.online && (
        <div className="meter-offline">
          <WifiOff />
          <div>
            <div className="meter-offline__title">Offline</div>
            <div className="meter-offline__sub">
              Ultima lettura {ageLabel(sensor.ageMinutes)}{sensor.lastSeenMs ? ` (${lastSeenAbsolute(sensor.lastSeenMs)})` : ''}.
            </div>
          </div>
        </div>
      )}

      {sensor.online && !isElectrical && (
        <>
          <div className="dpanel__section-label">Letture attuali</div>
          <div className="dpanel__metrics">
            {r.temperature != null && (
              <div className="metric">
                <div className="metric__k">Temperatura</div>
                <div className="metric__v">
                  <span className="num">{r.temperature.value.toFixed(1)}</span>
                  <span className="u">°C</span>
                </div>
              </div>
            )}
            {r.humidity != null && (
              <div className="metric">
                <div className="metric__k">Umidità</div>
                <div className="metric__v">
                  <span className="num">{r.humidity.value.toFixed(1)}</span>
                  <span className="u">%</span>
                </div>
              </div>
            )}
            {r.battery?.percentage != null && (
              <div className="metric">
                <div className="metric__k">Batteria</div>
                <div className="metric__v">
                  <span className="num">{clampPercent(r.battery.percentage).toFixed(0)}</span>
                  <span className="u">%</span>
                </div>
              </div>
            )}
            {r.rssi != null && (
              <div className="metric">
                <div className="metric__k">Segnale</div>
                <div className="metric__v">
                  <span className="num">{r.rssi.toFixed(0)}</span>
                  <span className="u">dB</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {sensor.online && isElectrical && (
        <>
          <div className="dpanel__section-label">Totale</div>
          <div className="dpanel__metrics dpanel__metrics--3">
            <div className="metric metric--hero">
              <div className="metric__k">Potenza</div>
              <div className="metric__v">
                <span className="num">{formatKW(r.power)}</span> <span className="u">kW</span>
              </div>
            </div>
            <div className="metric">
              <div className="metric__k">Potenza apparente</div>
              <div className="metric__v">
                <span className="num">{r.apparent != null ? (r.apparent / 1000).toFixed(1) : '—'}</span>
                <span className="u">kVA</span>
              </div>
            </div>
            <div className="metric">
              <div className="metric__k">Corrente</div>
              <div className="metric__v">
                <span className="num">{r.current != null ? r.current.toFixed(1) : '—'}</span>
                <span className="u">A</span>
              </div>
            </div>
            <div className="metric">
              <div className="metric__k">Tensione media</div>
              <div className="metric__v">
                <span className="num">{r.voltage != null ? r.voltage.toFixed(1) : '—'}</span>
                <span className="u">V</span>
              </div>
            </div>
            <div className="metric">
              <div className="metric__k">Fattore di pot.</div>
              <div className="metric__v">
                <span className="num">{r.pf != null ? r.pf.toFixed(2) : '—'}</span>
              </div>
            </div>
          </div>

          {r.phases && r.phases.length > 0 && (
            <>
              <div className="dpanel__section-label">Per fase</div>
              <div className="phases phases--rich">
                {r.phases.map((p) => (
                  <div key={p.label} className="phase">
                    <div className="phase__label">Fase {p.label}</div>
                    <div className="phase__grid phase__grid--rich">
                      <div><span className="phase__k">P</span><span className="phase__v">{formatKW(p.power)}<span className="u"> kW</span></span></div>
                      <div><span className="phase__k">S</span><span className="phase__v">{(p.apparent / 1000).toFixed(1)}<span className="u"> kVA</span></span></div>
                      <div><span className="phase__k">V</span><span className="phase__v">{p.voltage.toFixed(1)}<span className="u"> V</span></span></div>
                      <div><span className="phase__k">I</span><span className="phase__v">{p.current.toFixed(1)}<span className="u"> A</span></span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="dpanel__footer">
        <div className="dpanel__footer-k">Sorgente</div>
        <div className="dpanel__footer-v">Sintropy EOS</div>
        <div className="dpanel__footer-k">Ultima lettura</div>
        <div className="dpanel__footer-v">{ageLabel(sensor.ageMinutes)}</div>
      </div>
    </div>
  )

  return <PanelShell asSheet={asSheet} onClose={onClose}>{body}</PanelShell>
}
